import {
  DASHBOARD_ACTIVITIES,
  DASHBOARD_ATTENDANCE,
  DASHBOARD_BENEFICIARIES,
  DASHBOARD_DATA,
  DASHBOARD_CERTIFICATES_RECEIVED,
  DASHBOARD_ECD_REGISTRATION_APPLICATIONS,
  DASHBOARD_ECD_REGISTRATION_EFFICIENCY,
  DASHBOARD_OUTSTANDING_DSD_ASSESSMENTS,
  DASHBOARD_REGISTRATION_COMPLIANCE,
  DASHBOARD_REGISTRATION_OUTCOMES,
  DASHBOARD_FUNDS,
  DASHBOARD_SCHOOLS,
  PROMOTION_RATE,
  SP_DASHBOARD_OUTCOMES,
  SUBJECT_PASS_RATE,
} from '../graphql';
import { formatPassRateData, mergeCounts } from '.';

export interface DashboardChart {
  query?: string;
  type: string;
  variables?: string;
  formatData: string;
  width?: number;
  height?: number;
  legend?: string;
  options?: string;
}

export interface DashboardCard {
  cardTitle: string;
  charts: DashboardChart[];
}

export interface DashboardColumn {
  columnSize: number;
  cards: DashboardCard[];
}

export interface DashboardSection {
  sectionTitle: string;
  sectionSize: number;
  columns: DashboardColumn[];
}

export interface DashboardRow {
  sections: DashboardSection[];
}

export interface DashboardConfig {
  rows: DashboardRow[];
}

export const queryLookup = {
  'Institution Certificates Received': DASHBOARD_CERTIFICATES_RECEIVED,
  'Institution ECD Registration Applications': DASHBOARD_ECD_REGISTRATION_APPLICATIONS,
  'Institution ECD Registration Efficiency': DASHBOARD_ECD_REGISTRATION_EFFICIENCY,
  'Institution Outstanding DSD Assessments': DASHBOARD_OUTSTANDING_DSD_ASSESSMENTS,
  'Institution Registration Outcomes': DASHBOARD_REGISTRATION_OUTCOMES,
  'Institution Registration Compliance': DASHBOARD_REGISTRATION_COMPLIANCE,
  'Service Provider Activities': DASHBOARD_ACTIVITIES,
  'Service Provider Attendance': DASHBOARD_ATTENDANCE,
  'Service Provider Beneficiaries': DASHBOARD_BENEFICIARIES,
  'Service Provider Funds': DASHBOARD_FUNDS,
  'Service Provider Outcomes': SP_DASHBOARD_OUTCOMES,
  'Service Provider Outputs': DASHBOARD_DATA,
  'Service Provider Promotion Rate': PROMOTION_RATE,
  'Service Provider Schools': DASHBOARD_SCHOOLS,
  'Service Provider Subject Pass Rate': SUBJECT_PASS_RATE,
};

export class Chart {
  id: string;
  query: string;
  type: string;
  variables: string;
  formatData: string;
  width: number;
  height: number;
  legend: string;
  options: string;

  static DEFAULTS = Object.freeze({
    id: '1',
    query: 'Service Provider Funds',
    type: 'bar',
    variables: JSON.stringify({ input: {} }),
    formatData: JSON.stringify({
      labels: ['Label A', 'Label B'],
      datasets: [
        {
          label: 'Chart Label',
          backgroundColor: 'rgb(223,183,80)',
          hoverBackgroundColor: 'rgb(223,183,80)',
          data: [60, 40],
        },
      ],
    }),
    width: 100,
    height: 50,
    legend: JSON.stringify({ display: false }),
    options: JSON.stringify({}),
  });

  static SP_QUERY_TYPES = Object.freeze(queryLookup);

  constructor(params = {}) {
    const { id, query, type, variables, formatData, width, height, legend, options } = {
      ...Chart.DEFAULTS,
      ...params,
    };
    this.id =
      id === '1'
        ? `_${Math.random()
            .toString(36)
            .substr(2, 9)}`
        : id;
    this.query = query;
    this.type = type;
    this.variables = variables;
    this.formatData = formatData;
    this.width = width;
    this.height = height;
    this.legend = legend;
    this.options = options;
  }

  merge(patch: any) {
    return new Chart({ ...this, ...patch });
  }
}

export class Card {
  id: string;
  cardTitle: string;
  charts: DashboardChart[];

  static DEFAULTS = Object.freeze({
    id: '1',
    cardTitle: 'Card Title',
    charts: [new Chart()],
  });

  constructor(params = {}) {
    const { id, cardTitle, charts } = {
      ...Card.DEFAULTS,
      ...params,
    };
    this.id =
      id === '1'
        ? `_${Math.random()
            .toString(36)
            .substr(2, 9)}`
        : id;
    this.cardTitle = cardTitle;
    this.charts = charts;
  }

  merge(patch: any) {
    return new Card({ ...this, ...patch });
  }
}

export class Column {
  id: string;
  columnSize: number;
  cards: DashboardCard[];

  static DEFAULTS = Object.freeze({
    id: '1',
    columnSize: 1,
    cards: [new Card()],
  });

  constructor(params = {}) {
    const { id, columnSize, cards } = {
      ...Column.DEFAULTS,
      ...params,
    };
    this.id =
      id === '1'
        ? `_${Math.random()
            .toString(36)
            .substr(2, 9)}`
        : id;
    this.columnSize = columnSize;
    this.cards = cards;
  }

  merge(patch: any) {
    return new Column({ ...this, ...patch });
  }
}

export class Section {
  id: string;
  sectionSize: number;
  sectionTitle: string;
  columns: DashboardColumn[];

  static DEFAULTS = Object.freeze({
    id: '1',
    sectionSize: 1,
    sectionTitle: 'Section Title',
    columns: [new Column()],
  });

  constructor(params = {}) {
    const { id, sectionSize, sectionTitle, columns } = {
      ...Section.DEFAULTS,
      ...params,
    };
    this.id =
      id === '1'
        ? `_${Math.random()
            .toString(36)
            .substr(2, 9)}`
        : id;
    this.sectionSize = sectionSize;
    this.sectionTitle = sectionTitle;
    this.columns = columns;
  }

  merge(patch: any) {
    return new Section({ ...this, ...patch });
  }
}

export class Row {
  id: string;
  sections: DashboardSection[];

  static DEFAULTS = Object.freeze({
    id: '1',
    sections: [new Section()],
  });

  constructor(params = {}) {
    const { id, sections } = {
      ...Row.DEFAULTS,
      ...params,
    };
    this.id =
      id === '1'
        ? `_${Math.random()
            .toString(36)
            .substr(2, 9)}`
        : id;
    this.sections = sections;
  }

  merge(patch: any) {
    return new Row({ ...this, ...patch });
  }
}

export const getChartData = (jsonData: any, data: any, type: string) => {
  let labelArr;

  if (Array.isArray(jsonData.labels)) {
    labelArr = jsonData.labels;
  } else {
    if (jsonData.labels.dataLabel && data[jsonData.labels.dataLabel]) {
      if (jsonData.labels.dataLabel === 'subjectPassRate') {
        labelArr = formatPassRateData(data[jsonData.labels.dataLabel]).map((value: any) => value[jsonData.labels.key]);
      } else if (
        ['totalSchoolsInApprovedSessions', 'totalBeneficiariesInApprovedSessions', 'totalApprovedSessions'].includes(
          jsonData.labels.dataLabel
        )
      ) {
        labelArr = mergeCounts([...data[jsonData.labels.dataLabel]])
          .map((value: any) => {
            if (value && value.count > 0) {
              return value[jsonData.labels.key];
            }
            return undefined;
          })
          .filter((value: any) => value);
      } else if (jsonData.labels.dataLabel === 'promotionRate') {
        labelArr = data[jsonData.labels.dataLabel].map(
          (value: any) =>
            `'${value[jsonData.labels.key[0]].slice(-2)} ${value[jsonData.labels.key[1]].match(/\b\w/g).join('')}`
        );
      } else {
        labelArr = data[jsonData.labels.dataLabel].map((value: any) => value[jsonData.labels.key]);
      }
    } else {
      labelArr = [];
    }
  }

  return {
    ...jsonData,
    labels: labelArr || [],
    datasets: jsonData.datasets.map((dataset: object, index: number) => {
      return {
        ...dataset,
        data: jsonData.datasets[index].data.map((dataLabel: string) => {
          if (data[dataLabel]) {
            if (type === 'table' && Array.isArray(data[dataLabel])) {
              return data[dataLabel].map((item: any) => {
                const result = {};
                jsonData.fields.forEach((field: string) => {
                  result[field] = item[field];
                });
                return result;
              });
            }

            if (type !== 'table' && Array.isArray(data[dataLabel])) {
              if (dataLabel === 'subjectPassRate') {
                return formatPassRateData(data[dataLabel]).map((value: any) => value[jsonData.fields[0]]);
              }

              if (dataLabel === 'totalApprovedSessions') {
                return mergeCounts([...data[dataLabel]])
                  .map((value: any) => {
                    if (value && value.count > 0) {
                      return value[jsonData.fields[0]];
                    }
                    return undefined;
                  })
                  .filter((value: any) => value);
              }

              return data[dataLabel].map((value: any) => jsonData.fields && value[jsonData.fields[0]]);
            }

            return data[dataLabel];
          }

          return 0;
        }),
      };
    }),
  };
};
